import { Box, Text, type TextProps } from '@ltvco/refresh-lib/theme';

const STATUS_YES = 'yes';

type StatusCardProps = {
  title: string;
  status: string | undefined;
  subWhenYes: string;
  subWhenNo: string;
};

type ValueCardProps = {
  title: string;
  value: string;
  sub?: string;
  valueColor?: TextProps['color'];
  subColor?: TextProps['color'];
};

export type DeviceInfoCardProps = StatusCardProps | ValueCardProps;

function isStatusCard(props: DeviceInfoCardProps): props is StatusCardProps {
  return 'subWhenYes' in props;
}

function formatDisplayValue(value: string): string {
  const slashIndex = value.indexOf('/');
  if (slashIndex === -1) {
    return value;
  }
  return value.slice(0, slashIndex).trim();
}

function resolveStatusDisplay(
  status: string | undefined,
  subWhenYes: string,
  subWhenNo: string
) {
  const isYes = status?.toLowerCase() === STATUS_YES;
  const statusColor = (
    isYes ? 'error.main' : 'success.main'
  ) as TextProps['color'];

  return {
    value: isYes ? 'Yes' : 'No',
    sub: isYes ? subWhenYes : subWhenNo,
    subPrefix: isYes ? '✗' : '✓',
    valueColor: statusColor,
    subColor: statusColor,
  };
}

export default function DeviceInfoCard(props: DeviceInfoCardProps) {
  const { title } = props;

  const {
    value,
    sub,
    subPrefix,
    valueColor = 'text.primary',
    subColor = 'text.secondary',
  } = isStatusCard(props)
    ? resolveStatusDisplay(props.status, props.subWhenYes, props.subWhenNo)
    : {
        value: props.value,
        sub: props.sub,
        subPrefix: undefined,
        valueColor: props.valueColor,
        subColor: props.subColor,
      };

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2.5,
        px: 2.5,
        py: 2,
        flex: 1,
        minWidth: 0,
      }}
    >
      <Text
        variant="overline"
        color="text.secondary"
        sx={{
          fontWeight: 700,
          fontSize: '0.65rem',
          letterSpacing: '0.12em',
          display: 'block',
          mb: 0.5,
        }}
      >
        {title}
      </Text>

      <Text
        color={valueColor}
        sx={{
          fontWeight: 700,
          fontSize: '1.75rem',
          lineHeight: 1.1,
          mb: 0.4,
        }}
      >
        {formatDisplayValue(value)}
      </Text>

      {sub && (
        <Text
          color={subColor}
          sx={{
            fontSize: '0.8rem',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {subPrefix && <span>{subPrefix}</span>}
          {sub}
        </Text>
      )}
    </Box>
  );
}
